import { useCallback, useEffect, useRef, useState } from "react";
import "./PhoneSlideshow.css";

// react-snap actually executes this component in a real (headless) browser
// and waits for it to settle before snapshotting the HTML — so by default
// our effects below (mounting screenshots 2-8, detecting aspect ratio,
// auto-advancing the index) have already fired by the time it captures
// the page. A real visitor's browser then hydrates that HTML starting
// from each state's *initial* value (false/null/0), which no longer
// matches what's already in the DOM — a hard hydration mismatch (React
// error #418) that forces a full client-side re-render and throws away
// everything prerendering was supposed to save.
//
// react-snap sets this exact user agent string on its crawler, so we can
// detect the crawl and skip anything that would change the DOM away from
// its initial-render shape, keeping the snapshot identical to what a
// fresh client hydration will produce.
const IS_REACT_SNAP =
	typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

/**
 * Cross-fades through a set of screenshots. The screenshots are expected
 * to already include their own device frame, so this renders the raw
 * images only (no drawn bezel/notch around them).
 *
 * screenshots: [{ src, alt }]
 * size: "desktop" | "mobile" — controls the sizing class, and which
 *   navigation affordance is offered (arrows on desktop, swipe on mobile)
 * interval: ms between slides
 * zoom: extra scale applied to each screenshot, used to crop out
 *   baked-in padding (1 = no crop, higher = crop more)
 * shiftY: vertical shift applied after zooming, e.g. "-4%" to trim more
 *   off the top than the bottom (useful when the source padding is
 *   uneven, like extra space above the device)
 * width/height: the screenshot files' actual intrinsic pixel size (all
 *   screenshots in the set must share the same size — they're the same
 *   device frame). This is what the frame's aspect-ratio and the <img>
 *   width/height attrs are derived from. It used to be auto-detected by
 *   loading screenshots[0] in a throwaway Image() and setting state once
 *   it decoded — which is why the frame had no size at all on first
 *   paint and then visibly snapped to its real size a beat later, on
 *   *every* page load (hydration mismatch or not — this had nothing to
 *   do with react-snap). Passing the known size in as a prop makes the
 *   aspect-ratio correct from the very first render, so there's nothing
 *   left to snap into place.
 */
export default function PhoneSlideshow({
	screenshots,
	size = "desktop",
	interval = 3500,
	zoom = 1.2,
	shiftY = "0%",
	width = 393,
	height = 852,
}) {
	const [index, setIndex] = useState(0);
	// Only the first screenshot mounts on initial render. All 8 images live
	// stacked in the DOM at once (that's how the cross-fade works), so
	// `loading="lazy"` does nothing for them — they're in the initial
	// viewport. Genuinely deferring their *mount* until just after first
	// paint is what actually keeps them off the critical rendering path
	// and out of the LCP measurement.
	const [restMounted, setRestMounted] = useState(false);

	const count = screenshots ? screenshots.length : 0;

	// Bumped on every manual navigation (arrow click or swipe) so the
	// auto-advance effect below tears down its old timer and starts a
	// fresh interval — otherwise a manual nudge could be immediately
	// undone by an auto-advance tick a moment later.
	const [autoAdvanceKey, setAutoAdvanceKey] = useState(0);

	const goTo = useCallback(
		(next) => {
			if (count < 2) return;
			setIndex(((next % count) + count) % count);
			setAutoAdvanceKey((k) => k + 1);
		},
		[count],
	);
	const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);
	const goNext = useCallback(() => goTo(index + 1), [goTo, index]);

	// Defer mounting screenshots 2-8 until the browser is idle (or a short
	// timeout as a fallback), well before the slideshow's own interval
	// would ever need them.
	useEffect(() => {
		if (IS_REACT_SNAP) return undefined;
		if (typeof window === "undefined") return undefined;
		const schedule =
			window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
		const cancel = window.cancelIdleCallback || clearTimeout;
		const id = schedule(() => setRestMounted(true));
		return () => cancel(id);
	}, []);

	useEffect(() => {
		if (IS_REACT_SNAP) return undefined;
		if (!screenshots || screenshots.length < 2) return undefined;
		const id = setInterval(() => {
			setIndex((prev) => (prev + 1) % screenshots.length);
		}, interval);
		return () => clearInterval(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [screenshots, interval, autoAdvanceKey]);

	// Mobile: swipe left/right to change slides. Only acts on a
	// predominantly-horizontal drag past a small threshold, so it doesn't
	// fight the page's vertical scroll.
	const touchStart = useRef(null);

	function handleTouchStart(e) {
		const t = e.touches[0];
		touchStart.current = { x: t.clientX, y: t.clientY };
	}

	function handleTouchEnd(e) {
		const start = touchStart.current;
		touchStart.current = null;
		if (!start) return;
		const t = e.changedTouches[0];
		const dx = t.clientX - start.x;
		const dy = t.clientY - start.y;
		const SWIPE_THRESHOLD = 40;
		if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) {
			return;
		}
		if (dx < 0) {
			goNext();
		} else {
			goPrev();
		}
	}

	const showArrows = size === "desktop" && count > 1;
	const showSwipe = size === "mobile" && count > 1;

	return (
		<div
			className={`phone-frame phone-frame--${size}`}
			style={{ aspectRatio: `${width} / ${height}` }}
			onTouchStart={showSwipe ? handleTouchStart : undefined}
			onTouchEnd={showSwipe ? handleTouchEnd : undefined}
		>
			<div className="phone-frame-clip">
				{screenshots.map((shot, i) => {
					// Skip mounting screenshots 2-8 until we're past first paint.
					if (i > 0 && !restMounted) return null;

					return (
						<img
							key={shot.alt || i}
							alt={shot.alt || `Screenshot ${i + 1}`}
							src={shot.src}
							width={width}
							height={height}
							className={`phone-frame-img${i === index ? " is-active" : ""}`}
							style={{
								transform: `translateY(${shiftY}) scale(${zoom})`,
							}}
							loading={i === 0 ? "eager" : "lazy"}
							decoding="async"
							fetchPriority={i === 0 ? "high" : "auto"}
						/>
					);
				})}
			</div>

			{showArrows && (
				<>
					<button
						type="button"
						className="phone-frame-arrow phone-frame-arrow--prev"
						onClick={goPrev}
						aria-label="Previous screenshot"
					>
						<svg viewBox="0 0 24 24" aria-hidden="true">
							<path
								d="M15 5l-7 7 7 7"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<button
						type="button"
						className="phone-frame-arrow phone-frame-arrow--next"
						onClick={goNext}
						aria-label="Next screenshot"
					>
						<svg viewBox="0 0 24 24" aria-hidden="true">
							<path
								d="M9 5l7 7-7 7"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
				</>
			)}
		</div>
	);
}
